import { FC, useState, useEffect, useRef, useMemo } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { CircularProgress } from '@mui/material';

import { useTourContext } from 'src/presentations/guided-tours';
import { useSavingsStats } from 'src/use-cases/performance/usePerformance';
import { ModalityProviderSavings } from './SectionDetail/ModalityProviderSavings';
import { getProviderModalityCombinations, getSavingStatsList } from './helpers';

import { LoaderSavings } from './LoaderSavings';
import { CardStat } from './Stat/CardStat';

interface PerformanceContentSavingsProps {
  dataFilter: Record<string, any>;
}

export const PerformanceContentSavings: FC<PerformanceContentSavingsProps> = ({ dataFilter }) => {
  const { watch } = useFormContext();
  const formValues = watch();
  const [visibleCount, setVisibleCount] = useState(2);
  const [firstVisibleIdx, setFirstVisibleIdx] = useState(0);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tour = useTourContext().appTour.registerTour('performanceAssessment');

  const { data: dataStats, isLoading: isLoadingStats } = useSavingsStats({
    filterConfigs: formValues,
  });

  const statsList = useMemo(() => getSavingStatsList(dataStats), [dataStats]);

  // Get combinations for this component
  const combinations = useMemo(() => getProviderModalityCombinations(formValues, dataFilter), [formValues, dataFilter]);

  // Reset visible count and first visible index when combinations change
  useEffect(() => {
    setVisibleCount(2);
    setFirstVisibleIdx(0);
  }, [combinations.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && visibleCount < combinations.length) {
          setVisibleCount((prev) => Math.min(prev + 2, combinations.length));
        }
      },
      {
        threshold: 0.1,
        rootMargin: '20px',
      }
    );

    // Add timeout to ensure DOM elements are rendered after tab switch
    const timeoutId = setTimeout(() => {
      const currentRef = loadMoreRef.current;
      if (currentRef) {
        observer.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [visibleCount, combinations.length]);

  return (
    <div>
      {isLoadingStats ? (
        <LoaderSavings />
      ) : (
        <div className='pt-8 px-16 xl:px-32 pb-20 xl:pb-24 border-b border-neutral-20'>
          <div id={tour.registerStep('topLevelMetrics')} className='grid gap-18 xl:gap-24 sm:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4'>
            {statsList.map((item) => (
              <CardStat key={`card-stat-main-${item?.type}`} type={item.type} values={item.values} />
            ))}
          </div>
        </div>
      )}

      <div className='border-t border-white px-16 xl:px-32 py-20 xl:py-24 flex flex-col gap-32 xl:gap-40'>
        {combinations.slice(0, visibleCount).map((item: any, index: number) => (
          <ModalityProviderSavings
            key={`performance-${item.provider}-${item.modality}`}
            provider={item.provider}
            modality={item.modality}
            isActive
            isFirstItem={index === firstVisibleIdx}
            onHide={index === firstVisibleIdx ? () => setFirstVisibleIdx((v) => v + 1) : undefined}
          />
        ))}
        {visibleCount < combinations.length && (
          <div ref={loadMoreRef} className='text-center py-12'>
            <CircularProgress size={24} />
          </div>
        )}
      </div>
    </div>
  );
};
